import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { usesAgencyStaffNav } from '@/utils/bottomNavMode';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { useNavGating } from '@/hooks/useNavGating';
import { LockBadge } from '@/components/gating/LockBadge';
import { useMemo } from 'react';
import { useNavReleaseBadges } from '@/hooks/useReleaseSections';
import { NavCountBadge, useNavBadgeAria } from '@/components/layout/NavCountBadge';

/**
 * Bottom Navigation Component
 *
 * Mobile bottom navigation bar (<1024px)
 * - Fixed bottom position
 * - Primary items shown directly, overflow items in "More" popover
 * - Active item: Primary color + label
 * - Inactive items: Gray + smaller label
 * - Uses HappyIcons icon font
 * - Shows different items for agency staff vs regular users
 */

interface NavItem {
  id: string;
  label: string;
  shortLabel: string;
  href: string;
  icon: string;
  visible?: boolean;
  /** If true, this item goes in the "More" menu instead of the main bar */
  overflow?: boolean;
}

export const BottomNav = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  // Plan-gated pages (Brand Check, Blog, Analytics) stay in the nav when locked: lock badge, locked page on open
  const navGate = useNavGating();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const releaseBadges = useNavReleaseBadges();
  const badgeAria = useNavBadgeAria();

  const variationsEnabled = user?.currentTeam?.variationsEnabled ?? user?.team?.variationsEnabled ?? false;

  // Regular user navigation
  const regularNavItems: NavItem[] = useMemo(
    () => [
      {
        id: 'dashboard',
        label: t('nav_dashboard'),
        shortLabel: t('nav_short_dashboard'),
        href: '/',
        icon: 'hm-dashboard',
        visible: true,
      },
      {
        id: 'calendar',
        label: t('nav_calendar'),
        shortLabel: t('nav_short_calendar'),
        href: '/calendar',
        icon: 'hm-calendar',
        visible: true,
      },
      {
        id: 'creator',
        label: t('nav_creator'),
        shortLabel: t('nav_short_creator'),
        href: '/creator',
        icon: 'hm-magic-wand',
        visible: true,
      },
      {
        id: 'brand-check',
        label: t('nav_brand_check'),
        shortLabel: t('nav_short_brand_check'),
        href: '/brand-check',
        icon: 'hm-radar',
        visible: true,
      },
      {
        id: 'analytics',
        label: t('nav_analytics'),
        shortLabel: t('nav_short_analytics'),
        href: '/analytics',
        icon: 'hm-graph',
        visible: true,
      },
      // Overflow items — go in "More" menu
      {
        id: 'media-library',
        label: t('media_library'),
        shortLabel: t('nav_short_media'),
        href: '/media-library',
        icon: 'hm-photo-stack',
        visible: true,
        overflow: true,
      },
      {
        id: 'blog',
        label: t('nav_blog', { defaultValue: 'Blog' }),
        shortLabel: t('nav_short_blog', { defaultValue: 'Blog' }),
        href: '/blog',
        icon: 'hm-blog-content',
        visible: true,
        overflow: true,
      },
      {
        id: 'variations',
        label: t('nav_variations', { defaultValue: 'Variations' }),
        shortLabel: t('nav_short_variations', { defaultValue: 'Variations' }),
        href: '/variations',
        icon: 'hm-language-change',
        visible: true,
        overflow: true,
      },
      {
        id: 'approvals',
        label: t('nav_approvals'),
        shortLabel: t('nav_short_approvals'),
        href: '/approvals',
        icon: 'hm-clip-board',
        visible: true,
        overflow: true,
      },
    ],
    [t]
  );

  // Agency staff navigation — Portfolio is the single primary surface
  const agencyNavItems: NavItem[] = useMemo(
    () => [
      {
        id: 'agency-portfolio',
        label: t('nav_portfolio', { defaultValue: 'Portfolio' }),
        shortLabel: t('nav_short_portfolio', { defaultValue: 'Portfolio' }),
        href: '/agency/portfolio',
        icon: 'hm-board',
        visible: true,
      },
      {
        id: 'agency-settings',
        label: t('nav_agency_settings'),
        shortLabel: t('nav_short_settings'),
        href: '/agency/settings',
        icon: 'hm-tools',
        visible: true,
      },
    ],
    [t]
  );

  const isAgencyStaff = usesAgencyStaffNav(user);
  const allItems = isAgencyStaff ? agencyNavItems : regularNavItems;

  // Filter items based on feature flags
  const filteredItems = allItems.filter((item) => {
    if (item.id === 'approvals' && (!isAgencyTeam || carteBlanche)) return false;
    if (navGate(item.id)?.hidden) return false;
    if (item.id === 'variations' && !variationsEnabled) return false;
    return item.visible;
  });

  const primaryItems = filteredItems.filter((item) => !item.overflow);
  const overflowItems = filteredItems.filter((item) => item.overflow);
  const hasOverflow = overflowItems.length > 0;
  // The overflow items hide behind "More": flag it when one of them has something waiting
  const overflowBadge = overflowItems
    .map((item) => releaseBadges[item.id])
    .find((badge) => badge !== undefined);

  // Check if any overflow item is currently active
  const isOverflowActive = overflowItems.some((item) =>
    item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
  );

  return (
    <>
      {/* More menu popover */}
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-popover"
            onClick={() => setMoreOpen(false)}
          />
          <div className="fixed bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+var(--floating-stack-offset))] right-2 z-popover bg-white rounded-xl shadow-xl border border-gray-200 py-2 min-w-[180px]">
            {overflowItems.map((item) => {
              const isActive = item.href === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.href);
              const badge = releaseBadges[item.id];
              const gate = navGate(item.id);
              const isLocked = gate?.locked === true;
              return (
                <button
                  key={item.id}
                  {...(isLocked && gate
                    ? { 'data-testid': `locked-entry-${gate.feature}`, 'aria-label': t('gating_locked_label', { feature: item.label }) }
                    : {})}
                  onClick={() => {
                    setMoreOpen(false);
                    navigate(item.href);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 max-sm:min-h-[44px] text-sm transition-colors ${
                    isActive
                      ? 'text-primary-700 bg-primary-50 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="relative inline-flex">
                    <i className={`hm ${item.icon} text-[18px] md:text-[20px] leading-none`} />
                    {isLocked && <LockBadge size="sm" className="absolute -right-1.5 -bottom-1.5" />}
                  </span>
                  {item.label}
                  {badge && (
                    <span className="relative ml-auto inline-flex h-[18px] min-w-[18px]">
                      <NavCountBadge count={badge.count} kind={badge.kind} className="inset-0" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Bottom nav bar */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 pb-[env(safe-area-inset-bottom)]"
        aria-label={t('aria_mobile_navigation')}
      >
        <div className="flex items-center justify-around px-2">
          {primaryItems.map((item) => {
            const badge = releaseBadges[item.id];
            const gate = navGate(item.id);
            const isLocked = gate?.locked === true;
            const baseLabel = isLocked ? t('gating_locked_label', { feature: item.label }) : item.label;
            return (
            <NavLink
              key={item.id}
              to={item.href}
              end
              className="flex-1 min-h-[44px] min-w-[44px]"
              aria-label={badge ? `${baseLabel}, ${badgeAria(badge.count, badge.kind)}` : baseLabel}
              {...(isLocked && gate ? { 'data-testid': `locked-entry-${gate.feature}` } : {})}
              onClick={() => setMoreOpen(false)}
            >
              {({ isActive }) => (
                <div className="flex flex-col items-center justify-center gap-1 py-3 md:gap-1.5 md:py-4">
                  <span className="relative inline-flex">
                    <i
                      className={`hm ${item.icon} text-[24px] md:text-[30px] leading-none ${
                        isActive ? 'text-primary-500' : 'text-gray-500'
                      }`}
                    />
                    {badge && (
                      <NavCountBadge count={badge.count} kind={badge.kind} className="-right-2.5 -top-2" />
                    )}
                    {isLocked && <LockBadge size="sm" className="absolute -right-2 -bottom-1.5" />}
                  </span>
                  <span
                    className={`text-[9px] md:text-[12px] font-medium ${
                      isActive ? 'text-primary-500' : 'text-gray-600'
                    }`}
                  >
                    {item.shortLabel}
                  </span>
                </div>
              )}
            </NavLink>
            );
          })}

          {/* More button */}
          {hasOverflow && (
            <button
              className="flex-1 min-h-[44px] min-w-[44px]"
              onClick={() => setMoreOpen(!moreOpen)}
              aria-label={
                overflowBadge
                  ? `${t('more', { defaultValue: 'More' })}, ${badgeAria(overflowBadge.count, overflowBadge.kind)}`
                  : t('more', { defaultValue: 'More' })
              }
              aria-expanded={moreOpen}
            >
              <div className="flex flex-col items-center justify-center gap-1 py-3 md:gap-1.5 md:py-4">
                <span className="relative inline-flex">
                  <i
                    className={`hm hm-ellipsis-horizontal text-[24px] md:text-[30px] leading-none ${
                      moreOpen || isOverflowActive ? 'text-primary-500' : 'text-gray-500'
                    }`}
                  />
                  {overflowBadge && (
                    <NavCountBadge
                      count={overflowBadge.count}
                      kind={overflowBadge.kind}
                      dot
                      className="-right-1 -top-1"
                    />
                  )}
                </span>
                <span
                  className={`text-[9px] md:text-[12px] font-medium ${
                    moreOpen || isOverflowActive ? 'text-primary-500' : 'text-gray-600'
                  }`}
                >
                  {t('more', { defaultValue: 'More' })}
                </span>
              </div>
            </button>
          )}
        </div>
      </nav>
    </>
  );
};
